import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import {
  PageReader,
  type PageReaderProps,
  READER_TEST_IDS,
  asStyle,
  loadExperienceFonts,
  readerMotion,
  readerPlainText,
  readerThemeStyle,
  usePrefersReducedMotion,
} from '@/components/reader';
import { ReaderBody } from '@/components/reader/reader-body';
import { READER_VARIABLE_CLASS, readerPreviewSegments } from '@/components/reader/reader-text';
import { useAdminSession } from '@/lib/admin-session';
import { fetchPersonas } from '@/lib/api';
import {
  CONVERSATION_CONTEXT_EMPTY_BUBBLE,
  CONVERSATION_WITNESS_BUBBLE,
  interviewerBringsSentence,
} from '@/lib/experience-labels';
import type { ExperiencePageDraft } from '@matter_extractor/shared';
import {
  FIRST_TEMPS_START,
  conversationItemsOf,
  isConversationPage,
  resolveTheme,
} from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';

/**
 * Une page jouée dans la conversation, RENDUE EN FIL (prolongation.md §6 ;
 * planche `editeur.html`, sections 1 et 3 ; issue #186).
 *
 * Trois surfaces la montent - l'aperçu de l'éditeur de page, la scène de la
 * Timeline avec ses vignettes, l'aperçu plein écran - et c'est le MÊME composant
 * partout, par `PageOrThread`, qui remplace `PageReader` à chaque endroit où une
 * page peut être une page du fil. Peint par le thème, comme la conversation de
 * la personne : le cadre porte les propriétés `--lecteur-*` de la page, les bulles
 * les lisent, rien de l'admin n'y entre.
 *
 * ## Ce qu'on y voit, du haut vers le bas
 *
 * 1. une **bulle témoin**, toujours la même, qui situe le fil - ce que
 *    l'interviewer vient de dire, sans que personne ne l'ait écrit ;
 * 2. à la place de la bulle de l'interviewer, le **contexte** de la page dans une
 *    bulle en POINTILLÉ et en italique, titrée « Jeanne amène la page » : la
 *    bulle ne peut pas s'écrire sans session, donc l'aperçu montre ce que
 *    l'interviewer recevra, et jamais une bulle inventée (PH15) ;
 * 3. les éléments de la page, `conversationItemsOf` : Texte, Image et Son sont
 *    masqués, les temps aplatis, dans l'ordre de la page.
 *
 * **Jamais d'appel de modèle**, et rien de cette page n'en peut déclencher un.
 *
 * ## Le contexte est « résolu » comme tout texte d'un aperçu : par son NOM
 *
 * D5 (grill Variables, `reader-text.ts`) interdit à un aperçu d'inventer une
 * valeur : un jeton se dessine en pastille qui porte le nom de la variable, et le
 * seul aperçu qui en résout un est le plein écran, par le résolveur PARTIEL qu'il
 * passe - un champ tapé par la personne qui regarde. La planche dit « Camille » à
 * la place de `@prénom` : c'est son exemple fictif, pas une valeur que l'éditeur
 * possède. Ce composant passe donc à `readerPreviewSegments` les deux fonctions
 * que l'écran lui a données et ne décide rien.
 *
 * ## Les éléments sont montés par `ReaderBody`, dans un cadre qui est le nôtre
 *
 * Le corps du lecteur dessine les blocs - leur rythme, leur cadre, leur
 * sélection en `edit`, leur condition d'affichage - et c'est ce qu'il faut ici
 * aussi : « il n'existe pas deux orthographes d'un bloc » (§9.2). Il rend sa
 * propre colonne, avec sa marge et sa largeur ; les trois propriétés qui la
 * règlent sont remises à zéro sur le conteneur qui l'enveloppe, pour que les
 * éléments s'alignent sous la bulle - dans la colonne des bulles, après l'avatar
 * - au lieu de se recentrer deux fois.
 *
 * La page que le corps reçoit est une page DÉRIVÉE : un seul temps, qui porte les
 * éléments du fil, et aucune durée automatique - une page du fil n'avance qu'au
 * clic. Les temps de la vraie page sont ignorés, et c'est ce que la pile dit
 * (« ignoré dans le fil »).
 *
 * ## Le prénom du persona vient de la bibliothèque, sans que la page le porte
 *
 * La page ne sait rien de l'expérience. Les trois montages qui tiennent un
 * brouillon posent `InterviewerPersona` autour de leur sous-arbre, avec
 * l'identifiant du persona qu'il choisit ; le titre de la bulle le lit dans la
 * liste que l'onglet Persona écrit déjà (`['personas', email]`, la même clé, donc
 * aucune lecture de plus). Un persona anonyme, absent ou pas encore lu dit
 * « L'interviewer » : une lecture en vol ne doit jamais donner un titre faux.
 */

/** The persona the experience chose, for the title of the context bubble - null for none. */
const InterviewerPersonaContext = createContext<string | null>(null);

/**
 * Pose le persona de l'expérience sous ce sous-arbre.
 *
 * Aucun appel réseau ici : c'est `ConversationThread` qui lit la liste, et
 * seulement quand une page du fil est dessinée - une expérience sans page du fil
 * ne paie rien.
 */
export function InterviewerPersona({
  personaId,
  children,
}: {
  readonly personaId: string | null;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <InterviewerPersonaContext.Provider value={personaId}>
      {children}
    </InterviewerPersonaContext.Provider>
  );
}

/** The first name the person sees, or null: anonymous, not chosen, or not read yet. */
function useInterviewerFirstName(): string | null {
  const personaId = useContext(InterviewerPersonaContext);
  const { email } = useAdminSession();
  const library = useQuery({
    queryKey: ['personas', email],
    queryFn: fetchPersonas,
    enabled: personaId !== null,
    retry: false,
  });
  if (personaId === null) return null;
  const card = library.data?.personas.find((one) => one.id === personaId);
  return card?.presentation.kind === 'named' ? card.presentation.firstName : null;
}

/**
 * La page que `ReaderBody` joue : les éléments du fil dans UN temps, sans durée.
 *
 * Exportée pour être mesurée sans DOM : c'est là que se décide ce qu'une page du
 * fil montre, par `conversationItemsOf` de `shared` - la fonction que le lecteur
 * de la personne et l'enregistrement lisent aussi.
 */
export function threadPageOf(page: ExperiencePageDraft): ExperiencePageDraft {
  const first = page.temps[0];
  return {
    ...page,
    autoDurationSeconds: null,
    temps: [
      {
        id: first?.id ?? `${page.id}-fil`,
        speed: first?.speed ?? 'normal',
        start: FIRST_TEMPS_START,
        blocks: conversationItemsOf(page),
      },
    ],
  };
}

/* ------------------------------------------------------------------ the thread */

/** The avatar's width and its gap to the bubble: the items stand under the bubble, not under it. */
const AVATAR_OFFSET = '40px';

const THREAD: CSSProperties = {
  justifyContent: 'flex-end',
  gap: '10px',
};

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  alignSelf: 'flex-start',
  maxWidth: '92%',
  minWidth: 0,
};

const AVATAR: CSSProperties = {
  display: 'inline-flex',
  flex: '0 0 auto',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '999px',
  backgroundColor: 'var(--lecteur-color-accent)',
  color: 'var(--lecteur-color-button-text)',
  fontSize: 'var(--lecteur-small-size)',
  fontWeight: 600,
};

// A bubble's radius is a FIXED 14 px, like the conversation of the person: the
// three shapes of §9.4 are BUTTON shapes, and a pill would draw a lozenge here.
const BUBBLE: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: '14px',
  borderBottomLeftRadius: '6px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const WITNESS: CSSProperties = {
  ...BUBBLE,
  backgroundColor: 'var(--lecteur-color-ai-bubble)',
};

/** The context bubble: dashed, transparent, italic - what the model will READ, not what it said. */
const CONTEXT: CSSProperties = {
  ...BUBBLE,
  border: '1.5px dashed var(--lecteur-color-muted)',
  color: 'var(--lecteur-color-muted)',
  fontStyle: 'italic',
};

const CONTEXT_TITLE: CSSProperties = {
  display: 'block',
  marginBottom: '2px',
  color: 'var(--lecteur-color-text)',
  fontSize: 'var(--lecteur-small-size)',
  fontStyle: 'normal',
  fontWeight: 600,
};

/** The three properties that size the reader's own column: zeroed under the bubble. */
const ITEMS_COLUMN = {
  '--lecteur-pad-block': '0px',
  '--lecteur-pad-inline': '0px',
  '--lecteur-column': 'none',
  '--lecteur-column-choice': 'none',
} as const;

export function ConversationThread({
  page,
  theme,
  format,
  mode,
  values,
  selectedBlockId = null,
  onSelectBlock,
  onValueChange,
  onAdvance,
  resolveVariable,
  variableNames,
  conditions,
  conditionMark,
  noyau,
  waitingBlockId = null,
}: PageReaderProps): ReactNode {
  const firstName = useInterviewerFirstName();
  const visual = resolveTheme(theme, page.visual);
  const prefersReducedMotion = usePrefersReducedMotion();
  const motion = readerMotion(visual, prefersReducedMotion);

  /* The faces the theme names, as the reader asks for them - see `page-reader.tsx`. */
  useEffect(() => {
    void loadExperienceFonts(visual);
  }, [visual]);

  const context = page.conversation?.context ?? '';
  const segments =
    variableNames === undefined
      ? null
      : readerPreviewSegments(context, variableNames, resolveVariable);
  const plain = segments === null ? readerPlainText(context, resolveVariable) : null;
  const empty = context.trim() === '';

  const avatar =
    firstName === null ? null : (
      <span style={AVATAR} aria-hidden="true">
        {firstName.slice(0, 1).toUpperCase()}
      </span>
    );

  return (
    <div
      className="lecteur"
      data-testid={READER_TEST_IDS.root}
      data-format={format}
      data-mode={mode}
      data-motion={motion}
      data-conversation="true"
      style={asStyle(readerThemeStyle(visual, format))}
      inert={mode === 'static'}
    >
      <div
        className="lecteur-colonne"
        data-testid={EXPERIENCE_IDS.conversationThread}
        data-page-id={page.id}
        style={THREAD}
      >
        <div style={ROW}>
          {avatar}
          <p data-testid={EXPERIENCE_IDS.conversationWitness} style={WITNESS}>
            {CONVERSATION_WITNESS_BUBBLE}
          </p>
        </div>

        <div style={ROW}>
          {avatar}
          <p data-testid={EXPERIENCE_IDS.conversationContext} style={CONTEXT}>
            <span style={CONTEXT_TITLE}>{interviewerBringsSentence(firstName)}</span>
            {empty
              ? CONVERSATION_CONTEXT_EMPTY_BUBBLE
              : segments === null
                ? plain
                : segments.map((segment, index) =>
                    segment.kind === 'text' ? (
                      // biome-ignore lint/suspicious/noArrayIndexKey: the segments are a read-only run of one string.
                      <Fragment key={index}>{segment.text}</Fragment>
                    ) : (
                      <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: the segments are a read-only run of one string.
                        key={index}
                        className={READER_VARIABLE_CLASS}
                        data-testid="lecteur-variable"
                      >
                        {segment.label}
                      </span>
                    ),
                  )}
          </p>
        </div>

        <div
          data-testid={EXPERIENCE_IDS.conversationItems}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: avatar === null ? 0 : AVATAR_OFFSET,
            ...asStyle(ITEMS_COLUMN),
          }}
        >
          <ReaderBody
            page={threadPageOf(page)}
            theme={theme}
            mode={mode}
            motion={motion}
            play={mode === 'interactive'}
            gestured={false}
            values={values}
            selectedBlockId={selectedBlockId}
            onSelectBlock={onSelectBlock}
            onValueChange={onValueChange}
            onAdvance={onAdvance}
            resolveVariable={resolveVariable}
            variableNames={variableNames}
            revealed={undefined}
            onRevealed={undefined}
            conditions={conditions}
            conditionMark={conditionMark}
            noyau={noyau}
            waitingBlockId={waitingBlockId}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * `PageReader`, ou le fil quand la page en est une (§6) - le seul aiguillage.
 *
 * Il prend les propriétés du lecteur telles quelles : chaque endroit qui montait
 * `PageReader` pour un aperçu monte celui-ci à la place, et ne sait pas lequel
 * des deux dessine. Ce qu'une page du fil ne joue pas - `revealed`, `replayKey`,
 * `thanks` - est ignoré par `ConversationThread`, jamais refusé.
 */
export function PageOrThread(props: PageReaderProps): ReactNode {
  return isConversationPage(props.page) ? (
    <ConversationThread {...props} />
  ) : (
    <PageReader {...props} />
  );
}
